import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../../api/document';
import { DocumentStatus } from '../../../../../../api/model/document';
import { PeriodicReview } from '../../../../../../api/model/periodicReview';
import { ButtonGroupContext } from '../../../../Contexts/ButtonGroupContext';
import { CredentialConfirmationModal } from '../../../../CredentialConfirmationModal/index';
import { DocumentOverviewContext } from '../../../Context';

type ApproveButtonProps = {
  periodicReview?: PeriodicReview;
  refetchPeriodicReview: () => void;
};

export const ApproveButton = (props: ApproveButtonProps) => {
  const { periodicReview, refetchPeriodicReview } = props;
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { showToast } = useToastProvider();

  const openModal = () => {
    setDisabled(true);
    setIsConfirmationModalOpen(true);
  };

  const closeModal = () => {
    setDisabled(false);
    setIsConfirmationModalOpen(false);
  };

  const approveDocument = (
    email: string,
    password: string,
    comment: string,
  ) => {
    return documentApi
      .changeStatus(qualioDocument.id, {
        status: DocumentStatus.Approved,
        email,
        password,
        comment,
      })
      .then(() => {
        refetchDocumentOverviewData();
      });
  };

  const approvePeriodicReview = (
    email: string,
    password: string,
    comment: string,
  ) => {
    return documentApi
      .approvePeriodicReview(currentUser, qualioDocument.id, {
        email,
        password,
        comment,
      })
      .then(() => {
        refetchPeriodicReview();
        refetchDocumentOverviewData();
      });
  };

  const handleApproval = async (
    email: string,
    password: string,
    comment = '',
  ) => {
    try {
      if (periodicReview) {
        await approvePeriodicReview(email, password, comment);
      } else {
        await approveDocument(email, password, comment);
      }

      setDisabled(false);
      setIsConfirmationModalOpen(false);

      showToast({
        'data-cy': 'approve-button-approve-document-success-toast',
        id: 'approve-document-toast',
        status: 'success',
        title: t('notifications.success'),
        description: t('success.approvalActionCompleted'),
        replace: true,
      });
    } catch (error: unknown) {
      throw error;
    }
  };

  const handleApprovalError = () => {
    showToast({
      'data-cy': 'approve-button-approve-document-error-toast',
      id: 'approve-document-toast',
      status: 'error',
      title: t('notifications.error'),
      description: t('documentActions.failedToApproveDocument'),
      replace: true,
    });
  };

  return (
    <>
      <QButton
        data-cy="document-approve-button"
        isDisabled={isDisabled}
        onClick={openModal}
      >
        {t('buttons.approve')}
      </QButton>
      <CredentialConfirmationModal
        isOpen={isConfirmationModalOpen}
        onCloseModal={closeModal}
        handleStatusChange={handleApproval}
        handleError={handleApprovalError}
        modalHeading={t('modals.approveDocument')}
        modalText={t('modals.enterDigitalSignatureApprove')}
        buttonActionLabel={t('buttons.approve')}
      />
    </>
  );
};
